import { useEffect, useRef, useState } from 'react';

export interface GooglePayParamsProps {
  token: string | null;
  billingAddress: {
    name: string;
    address1: string;
    address2?: string;
    locality: string;
    administrativeArea: string;
    postalCode: string;
    countryCode: string;
  } | null;
}

interface GooglePayButtonProps {
  valueTotal: number;
  method: any;
  onCreateOrder: ({ token, billingAddress }: GooglePayParamsProps) => void;
  onCancel: CallableFunction;
}

declare global {
  interface Window {
    google: any;
  }
}

const GooglePayButton: React.FC<GooglePayButtonProps> = ({
  valueTotal,
  method,
  onCreateOrder,
  onCancel = () => {}
}) => {
  
  const containerRef = useRef<HTMLDivElement>(null);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);

  const paymentRequest = {
    apiVersion: 2,
    apiVersionMinor: 0,
    allowedPaymentMethods: [
      {
        type: 'CARD',
        parameters: {
          allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
          allowedCardNetworks: ['VISA', 'MASTERCARD'],
          billingAddressRequired: true,
          billingAddressParameters: {
            format: 'FULL', // ou 'MIN'
          }
        },
        tokenizationSpecification: {
          type: 'PAYMENT_GATEWAY',
          parameters: {
            gateway: method.gateway_merchant_name,
            gatewayMerchantId: method.gateway_merchant_id,
          },
        },
      },
    ],
    merchantInfo: {
      merchantName: method.google_pay_merchant_name,
      merchantId: method.google_pay_merchant_id,
    },
    transactionInfo: {
      totalPriceStatus: 'FINAL',
      totalPriceLabel: 'Total',
      totalPrice: valueTotal ? valueTotal.toFixed(2) : '0.00',
      currencyCode: 'BRL',
      countryCode: 'BR',
    },
  };

  useEffect(() => {
    // Verifica se o Google Pay está carregado
    if (
      typeof window === 'undefined' ||
      !window.google ||
      !window.google.payments ||
      !window.google.payments.api
    ) {
      console.warn('Google Pay não está disponível no momento.');
      setIsAvailable(false);
      return;
    }

    const paymentsClient = new window.google.payments.api.PaymentsClient({
      environment: 'PRODUCTION',
    });

    paymentsClient
      .isReadyToPay(paymentRequest)
      .then((res: { result: boolean }) => {
        setIsAvailable(res.result);

        if (res.result && containerRef.current) {
          const button = paymentsClient.createButton({
            buttonColor: 'default',
            buttonType: 'buy',
            buttonRadius: 100,
            buttonLocale: 'pt',
            buttonSizeMode: 'fill',
            onClick: async () => {
              try {
                const paymentData = await paymentsClient.loadPaymentData(paymentRequest);
                const token = paymentData?.paymentMethodData?.tokenizationData?.token;
                const billingAddress = paymentData?.paymentMethodData?.info?.billingAddress;
                onCreateOrder({token, billingAddress});
              } catch (err) {
                console.error('Erro no pagamento:', err);
                onCancel();
              }
            },
          });
          containerRef.current.innerHTML = '';
          containerRef.current.appendChild(button);
        }
      })
      .catch(() => setIsAvailable(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueTotal]);

  return (
    <>
      {isAvailable === true && (
        <div ref={containerRef} className="w-full h-full" />
      )}

      {isAvailable === false && (
        <div className="flex justify-center text-sm text-shopfood-error p-3 bg-gray-50 font-semibold rounded-full">
          Google Pay não disponível.
        </div>
      )}
    </>
  );
};

export default GooglePayButton;